import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Brain, Heart, Users, Sparkles, Phone, Mail, MapPin, Clock, CheckCircle2, Shield, Linkedin, User, Wind, Activity, CloudRain, UserCheck, Home as HomeIcon, TrendingUp, Smile, Compass, Briefcase, Target, Lightbulb, HeartHandshake, CloudOff, School, Quote, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_AMOUNT = 80000; // ₹800 in paise (Razorpay requires amount in smallest currency unit)

const Home = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [paymentMode, setPaymentMode] = useState('pay_later'); // 'pay_now' or 'pay_later'
  const [showCalendly, setShowCalendly] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    concern: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  useEffect(() => {
    document.title = 'MIND FRAME - Counselling Services';
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!consentGiven) {
      toast.error('Please agree to the informed consent terms');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bookings`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: parseInt(formData.age),
        concern: formData.concern,
        emergencyContact: formData.emergencyContact,
        emergencyPhone: formData.emergencyPhone,
        consentGiven: consentGiven,
        paymentMode: paymentMode
      });
      
      const data = response.data;
      setBookingId(data.id);
      
      if (paymentMode === 'pay_now' && data.razorpay_order_id) {
        // Open Razorpay payment gateway
        const options = {
          key: data.razorpay_key_id,
          amount: SESSION_AMOUNT,
          currency: "INR",
          name: "MIND FRAME",
          description: "Counselling Session Payment",
          order_id: data.razorpay_order_id,
          handler: async function (response) {
            // Verify payment
            try {
              await axios.post(`${API}/verify-payment`, {
                booking_id: data.id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });
              
              toast.success('Payment successful! Now schedule your session.');
              setShowBooking(false);
              setShowCalendly(true);
            } catch (error) {
              toast.error('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: formData.name,
            email: formData.email,
            contact: formData.phone
          },
          theme: {
            color: "#2563eb"
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        // Pay later option - directly show Calendly
        toast.success(data.message);
        setShowBooking(false);
        setShowCalendly(true);
      }
      
      // Reset form
      setConsentGiven(false);
      setPaymentMode('pay_later');
      setFormData({
        name: '',
        email: '',
        phone: '',
        age: '',
        concern: '',
        emergencyContact: '',
        emergencyPhone: ''
      });
    } catch (error) {
      // Error already handled by toast message
      toast.error(error.response?.data?.message || 'Failed to submit booking. Please try again.');
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-800">MIND FRAME</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-slate-600 hover:text-blue-600 transition-colors">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-slate-600 hover:text-blue-600 transition-colors">About</button>
            <button onClick={() => scrollToSection('services')} className="text-slate-600 hover:text-blue-600 transition-colors">Services</button>
            <button onClick={() => window.location.href = '/resources'} className="text-slate-600 hover:text-blue-600 transition-colors">Resources</button>
            <button onClick={() => scrollToSection('contact')} className="text-slate-600 hover:text-blue-600 transition-colors">Contact</button>
            <Button onClick={() => setShowBooking(true)} className="bg-blue-600 hover:bg-blue-700">Book Session</Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                Professional Counselling Services
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-800 leading-tight">
                Reframing thoughts.<br />
                <span className="text-blue-600">Restoring balance.</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                A safe and confidential space where you can explore your thoughts, emotions, and challenges without judgment.
              </p>
              <div className="flex gap-4">
                <Button onClick={() => setShowBooking(true)} size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8">
                  Book a Session
                </Button>
                <Button onClick={() => scrollToSection('about')} size="lg" variant="outline" className="text-lg px-8 border-blue-300 text-blue-700 hover:bg-blue-50">
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/30 to-teal-400/30 rounded-3xl blur-3xl"></div>
              <div className="relative rounded-3xl shadow-2xl bg-gradient-to-br from-blue-600/90 to-teal-600/90 backdrop-blur-sm p-12 flex items-center justify-center min-h-[500px]">
                <div className="text-center space-y-6">
                  <div className="inline-block">
                    <Sparkles className="w-16 h-16 text-white/80 mx-auto mb-6 animate-pulse" />
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                    Begin your journey to
                    <br />
                    <span className="text-yellow-200">the best version of you</span>
                  </h2>
                  <div className="w-20 h-1 bg-white/50 mx-auto rounded-full"></div>
                  <p className="text-xl text-white/90 font-light italic max-w-md mx-auto">
                    Every great change starts with a single step forward
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 px-6 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Shield, text: 'Confidential' },
            { icon: CheckCircle2, text: 'Professional' },
            { icon: Heart, text: 'Empathetic' },
            { icon: Clock, text: 'Flexible' }
          ].map((item) => (
            <div key={item.text} className="flex flex-col items-center gap-3 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center">
                <item.icon className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-slate-700 font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-teal-50 to-slate-100 rounded-3xl"></div>
              <div className="relative bg-gradient-to-br from-blue-50/50 to-teal-50/50 rounded-3xl shadow-2xl p-8 backdrop-blur-sm border border-blue-100">
                <img 
                  src="https://customer-assets.emergentagent.com/job_reframing-thoughts/artifacts/90mopgcm_professional_photo.jpg" 
                  alt="Gaurav - Professional Counsellor"
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-slate-800">About Me</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Hi, I'm <span className="font-semibold text-blue-600">Gaurav</span>, a counselling professional dedicated to supporting individuals through life's challenges.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                My approach is empathetic, non-judgmental, and client-centered. I work collaboratively with you to understand your experiences and help you build practical tools for emotional well-being.
              </p>
              <div className="space-y-4 pt-4">
                <h3 className="text-xl font-semibold text-slate-800">Areas of Service:</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { icon: User, name: 'Individual Counselling' },
                    { icon: Wind, name: 'Anxiety Management' },
                    { icon: Activity, name: 'Stress Management' },
                    { icon: CloudRain, name: 'Depression Support' },
                    { icon: Heart, name: 'Relationship Counselling' },
                    { icon: HomeIcon, name: 'Family Counselling' },
                    { icon: TrendingUp, name: 'Life Transitions' },
                    { icon: Smile, name: 'Self-Esteem & Confidence' },
                    { icon: Sparkles, name: 'Mindfulness & Resilience' },
                    { icon: Briefcase, name: 'Workplace Stress & Burnout' },
                    { icon: Compass, name: 'Personal Growth' },
                    { icon: Lightbulb, name: 'Behavioural Support' },
                    { icon: Target, name: 'Goal Setting & Motivation' },
                    { icon: HeartHandshake, name: 'Grief & Loss Support' },
                    { icon: School, name: 'Adolescent Psychology' }
                  ].map((service) => (
                    <div key={service.name} className="flex items-center gap-3 p-3 bg-gradient-to-br from-blue-50 to-teal-50 rounded-lg border border-blue-100 hover:shadow-md transition-shadow">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{service.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-6">
                <a 
                  href="https://www.linkedin.com/in/gaurav-juneja-84246b1a/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                  <span>Connect on LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-gradient-to-br from-blue-50/50 to-teal-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Services</h2>
            <p className="text-xl text-slate-600">Personalized support tailored to your needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                title: '1:1 Counselling',
                desc: 'Personalized sessions tailored to your unique needs and goals.',
                features: ['50-minute sessions', 'Confidential space', 'Goal-oriented approach']
              },
              {
                icon: Heart,
                title: 'Flexible Sessions',
                desc: 'Choose between online video sessions or in-person consultations.',
                features: ['Online & In-Person', 'Flexible scheduling', 'Same quality care']
              },
              {
                icon: Users,
                title: 'Support Areas',
                desc: 'Comprehensive support for various emotional and mental health concerns.',
                features: ['Anxiety & Stress', 'Burnout Recovery', 'Self-esteem']
              }
            ].map((service) => (
              <Card key={service.title} className="border-none shadow-xl bg-white/80 backdrop-blur-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-slate-800">{service.title}</CardTitle>
                  <CardDescription className="text-base text-slate-600">{service.desc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-slate-700">
                        <Sparkles className="w-4 h-4 text-teal-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Card className="inline-block border-2 border-blue-200 bg-blue-50/50 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-blue-700 mb-2">₹800 <span className="text-lg font-normal text-slate-600">per session</span></div>
                <p className="text-slate-600">Duration: 50 minutes | Mode: Online (Google Meet/Zoom) & In-Person</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Share Your Experience</h2>
          <p className="text-lg text-slate-600 mb-8">
            Your feedback helps us improve and guides others seeking support
          </p>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="flex-1 text-left space-y-4">
                <h3 className="text-2xl font-semibold text-slate-800">Love your experience?</h3>
                <p className="text-slate-600 leading-relaxed">
                  We'd be grateful if you could take a moment to share your experience on Google Reviews. 
                  Your honest feedback helps us serve you better and assists others in finding the support they need.
                </p>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="text-yellow-500 text-2xl">★★★★★</span>
                  <span>Scan the QR code to leave a review</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="bg-white p-4 rounded-xl border-4 border-blue-600 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_reframing-thoughts/artifacts/gwkxiql2_Google%20Review%20Barcode.png"
                    alt="Google Review QR Code"
                    className="w-48 h-48 md:w-56 md:h-56"
                  />
                </div>
                <p className="text-center text-sm text-slate-600 mt-3 font-medium">
                  Scan to Review
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-slate-600">Real experiences from those who've embarked on their healing journey</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Priya S.',
                initial: 'P',
                rating: 5,
                text: 'Working with Gaurav has been transformative. The safe space created helped me explore my thoughts without judgment. I feel more equipped to handle life\'s challenges now.',
                role: 'Client'
              },
              {
                name: 'Rahul M.',
                initial: 'R',
                rating: 5,
                text: 'The counselling sessions were exactly what I needed during a difficult time. The empathetic approach and practical strategies have made a real difference in my daily life.',
                role: 'Client'
              },
              {
                name: 'Anjali K.',
                initial: 'A',
                rating: 5,
                text: 'I was hesitant at first, but the professional and compassionate guidance helped me work through my anxiety. I\'m grateful for the positive changes I\'ve experienced.',
                role: 'Client'
              }
            ].map((testimonial, idx) => (
              <Card key={idx} className="border-none shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-blue-50/50 to-teal-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xl font-bold">{testimonial.initial}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800">{testimonial.name}</h3>
                      <div className="flex gap-1 mt-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <Quote className="w-8 h-8 text-blue-200 mb-3" />
                  <p className="text-slate-600 leading-relaxed italic mb-4">
                    "{testimonial.text}"
                  </p>
                  <p className="text-sm text-slate-500">— {testimonial.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-sm text-slate-500 italic">
              * Names have been changed to protect client confidentiality
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">Get in Touch</h2>
            <p className="text-xl text-slate-600">Have questions? Feel free to reach out.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Phone, title: 'WhatsApp', info: '+91 99119 22763' },
              { icon: Mail, title: 'Email', info: 'hello@mfcounsellingservices.com' },
              { icon: MapPin, title: 'Sessions', info: 'Online & In-Person' }
            ].map((contact) => (
              <Card key={contact.title} className="text-center border-none shadow-lg bg-white hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <contact.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{contact.title}</h3>
                  <p className="text-slate-600">{contact.info}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-2xl">Book Your Session</CardTitle>
              <CardDescription>Please fill out the form below and review the consent agreement</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBookingSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age">Age *</Label>
                    <Input id="age" name="age" type="number" value={formData.age} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="concern">What brings you here today? *</Label>
                  <Textarea id="concern" name="concern" value={formData.concern} onChange={handleInputChange} rows={3} required />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContact">Emergency Contact Name *</Label>
                    <Input id="emergencyContact" name="emergencyContact" value={formData.emergencyContact} onChange={handleInputChange} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergencyPhone">Emergency Contact Phone *</Label>
                    <Input id="emergencyPhone" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleInputChange} required />
                  </div>
                </div>

                {/* Payment Mode Selection */}
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 space-y-4">
                  <h3 className="font-semibold text-lg text-slate-800">Payment Option</h3>
                  <p className="text-sm text-slate-600">Session Fee: ₹800 per session (50 minutes)</p>
                  <div className="space-y-3">
                    <div 
                      onClick={() => setPaymentMode('pay_now')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        paymentMode === 'pay_now' 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMode === 'pay_now' ? 'border-blue-600' : 'border-gray-400'
                        }`}>
                          {paymentMode === 'pay_now' && (
                            <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-800">Pay Now (₹800)</div>
                          <div className="text-sm text-slate-600">Pay online via UPI, Cards, Net Banking</div>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      onClick={() => setPaymentMode('pay_later')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        paymentMode === 'pay_later' 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMode === 'pay_later' ? 'border-blue-600' : 'border-gray-400'
                        }`}>
                          {paymentMode === 'pay_later' && (
                            <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-800">Pay After Session</div>
                          <div className="text-sm text-slate-600">Pay after completing your session</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Consent Agreement */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
                  <h3 className="font-semibold text-lg text-slate-800">Informed Consent Agreement</h3>
                  <div className="space-y-3 text-sm text-slate-700 max-h-60 overflow-y-auto pr-2">
                    <p><strong>1. Nature of Services:</strong> I understand that the services provided by MIND FRAME are counselling and psychological support services and are not a substitute for medical diagnosis, psychiatric treatment, or emergency services.</p>
                    <p><strong>2. Confidentiality:</strong> All information shared during sessions will be kept confidential, except in cases of risk of harm to self or others, legal requirements, or cases involving abuse or neglect.</p>
                    <p><strong>3. Voluntary Participation:</strong> I understand that my participation in counselling is voluntary and I may choose to discontinue at any time.</p>
                    <p><strong>4. Limitations:</strong> I acknowledge that outcomes of counselling cannot be guaranteed and depend on multiple factors including my active participation.</p>
                    <p><strong>5. Online Session Risks:</strong> I understand that online sessions may involve technical issues or potential confidentiality breaches despite best efforts.</p>
                    <p><strong>6. Professional Boundaries:</strong> I agree to maintain respectful and appropriate communication.</p>
                    <p><strong>7. Payment & Cancellation Policy:</strong> Session fee is ₹800 (50 minutes). Payment can be made online at booking or after the session. Cancellations must be made at least 24 hours in advance. If paid in advance, late cancellations or no-shows will be charged 50% of the session fee (₹400). Timely cancellations (24+ hours notice) with advance payment are eligible for a full refund.</p>
                    <p><strong>8. Emergency:</strong> I understand that MIND FRAME does not provide emergency services. In case of crisis, I will contact local emergency services or a mental health helpline.</p>
                  </div>
                  <div className="flex items-start gap-3 pt-4 border-t border-blue-200">
                    <Checkbox 
                      id="consent" 
                      checked={consentGiven} 
                      onCheckedChange={setConsentGiven}
                      className="mt-1"
                    />
                    <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                      I have read, understood, and agree to all the terms mentioned in the Informed Consent Agreement above.
                    </Label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    {paymentMode === 'pay_now' ? 'Proceed to Payment (₹800)' : 'Book Session'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowBooking(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Schedule Your Session</CardTitle>
                <CardDescription>Pick a time that works best for you</CardDescription>
              </div>
              <Button variant="ghost" onClick={() => setShowCalendly(false)} className="h-8 w-8 p-0">
                ✕
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <iframe 
                src="https://calendly.com/grv-juneja/30min" 
                width="100%" 
                height="600px" 
                frameBorder="0"
                title="Schedule Appointment"
              ></iframe>
            </CardContent>
            <div className="p-6 bg-blue-50 border-t">
              <p className="text-sm text-slate-600 text-center">
                After scheduling, you'll receive a confirmation email with session details.
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">MIND FRAME</span>
              </div>
              <p className="text-slate-400">Reframing thoughts. Restoring balance.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('home')} className="block text-slate-400 hover:text-white transition-colors">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-slate-400 hover:text-white transition-colors">About</button>
                <button onClick={() => scrollToSection('services')} className="block text-slate-400 hover:text-white transition-colors">Services</button>
                <button onClick={() => scrollToSection('contact')} className="block text-slate-400 hover:text-white transition-colors">Contact</button>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400">
                <p>WhatsApp: +91 99119 22763</p>
                <p>Email: hello@mfcounsellingservices.com</p>
                <a 
                  href="https://www.linkedin.com/in/gaurav-juneja-84246b1a/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  <span>LinkedIn Profile</span>
                </a>
                <p className="pt-2">All communication is treated with strict confidentiality.</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Leave a Review</h4>
              <p className="text-slate-400 text-sm mb-4">Scan to share your feedback on Google</p>
              <div className="bg-white p-3 rounded-lg inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_reframing-thoughts/artifacts/gwkxiql2_Google%20Review%20Barcode.png"
                  alt="Google Review QR Code"
                  className="w-32 h-32"
                />
              </div>
              <p className="text-slate-500 text-xs mt-2">Your feedback matters</p>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="bg-slate-800 rounded-lg p-6 mb-6">
              <h4 className="font-semibold mb-3 text-yellow-400">Important Disclaimer</h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                MIND FRAME provides counselling and emotional support services and does not offer medical or psychiatric treatment. 
                This is not a substitute for professional medical diagnosis or emergency services. If you are experiencing a crisis, 
                please contact your local emergency service (112) or mental health helpline immediately.
              </p>
            </div>
            <p className="text-center text-slate-500 text-sm">
              © 2024 MIND FRAME. All rights reserved. | <Link to="/privacy-policy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link> | <Link to="/terms-of-service" className="hover:text-slate-300 transition-colors">Terms of Service</Link> | <Link to="/intake" className="hover:text-slate-300 transition-colors">Intake Form</Link> | <a href="/MIND-FRAME-Intake-Form.pdf" target="_blank" rel="noreferrer" className="hover:text-slate-300 transition-colors">Download PDF</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
